import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalImageThumbnail from 'LuxKit/Modal/ModalImageThumbnail'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import type { ImageParams } from 'components/Common/ResponsiveImage/ResponsiveImage'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import { MaldivesFnfSpecialOfferLabel } from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectCalendar/MaldivesFnfSpecialOffer'
import { MALDIVES_MARKETING_BANNER_IMAGE_ID } from 'constants/charterFlight'
import useNavigateTo from 'hooks/useNavigateTo'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'

const imageParams: ImageParams = {
  aspectRatio: '16:8',
  mobileWidth: '720px',
  fit: 'center',
}

const MALDIVES_DIRECT_CAMPAIGN_PATH = '/campaign/maldives-direct'

function LandingPageLoyaltyExclusiveSavingsModal() {
  const intl = useIntl()
  const navigateTo = useNavigateTo()
  const image = useMemo<App.Image>(() => ({
    id: MALDIVES_MARKETING_BANNER_IMAGE_ID,
    title: intl.formatMessage({
      id: 'IE6SRQh6pFzvxwKoBXuh',
      defaultMessage: 'Aerial view of overwater villas in the Maldives',
      description: 'Alternative text for the hero image in the Infinite exclusive savings benefit modal',
    }),
  }), [intl])

  return (
    <ModalBase size="L">
      <ModalHeader title={intl.formatMessage({
        id: '624Khv7IkB_h1HoLoXHj',
        defaultMessage: 'Access to exclusive savings',
        description: 'Title for the Infinite member exclusive savings benefit details modal',
      })}
      />
      <ModalBody>
        <ModalImageThumbnail image={image} imageParams={imageParams} />
        <ModalContent>
          <Group direction="vertical" gap={32}>
            <VerticalSpacer gap={8}>
              <Heading variant="heading4">{intl.formatMessage({
                id: 'oHkOMSUiZeoJh1O8I0vn',
                defaultMessage: 'Exclusive rates on Maldives bundles',
                description: 'Section heading in the Infinite exclusive savings modal describing discounted Maldives bundles',
              })}
              </Heading>
              <BodyText variant="large">
                {intl.formatMessage({
                  id: 'N5ykH40ZUMO9mj7Q-Fwh',
                  defaultMessage: 'As an Infinite member, you get access to exclusive savings on selected Maldives hotel + flight bundles, including our direct Melbourne to Malé charter flights. Eligible packages may also include priority upgrade opportunities, depending on availability.',
                  description: 'Description of the exclusive Maldives bundle savings available to Infinite members in the benefit details modal',
                })}
              </BodyText>
            </VerticalSpacer>
            <VerticalSpacer gap={8}>
              <Heading variant="heading4">{intl.formatMessage({
                id: 'FVDYP-zofsM3qwtLHAyC',
                defaultMessage: 'How to find your Infinite Member price',
                description: 'Section heading explaining how Infinite members locate their exclusive member price in the calendar',
              })}
              </Heading>
              <BodyText variant="large">
                {intl.formatMessage({
                  id: 'NtfLubePjobLAvqopz1-',
                  defaultMessage: 'Choose a Maldives bundle, then open the date calendar. Look for dates with the badge “Infinite Member Price”. Prices vary by package, cabin and travel date.',
                  description: 'Explanation of how to find the Infinite Member price on the Maldives charter date calendar in the benefit details modal',
                })}
              </BodyText>
              <MaldivesFnfSpecialOfferLabel isInfiniteMember />
            </VerticalSpacer>
          </Group>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: intl.formatMessage({
            id: 'G-oPn4DfQlKARv9jll3i',
            defaultMessage: 'View offers',
            description: 'Button in the Infinite exclusive savings modal that navigates to the Maldives charter landing page',
          }),
          onClick: (_event, resolve) => {
            resolve()
            navigateTo(MALDIVES_DIRECT_CAMPAIGN_PATH)
          },
        }}
      />
    </ModalBase>
  )
}

export default LandingPageLoyaltyExclusiveSavingsModal
